import React, { useState } from "react";
import {
  Card,
  Typography,
  Divider,
  Row,
  Col,
  Image,
  Space,
  Alert,
  List,
  Button,
  Empty,
  Modal,
  Tag,
  message,
  Tooltip,
  Select,
  Tabs,
  Input,
  InputNumber,
  Spin,
} from "antd";
import { BulbOutlined, ExperimentOutlined } from "@ant-design/icons";
import AIService from "../services/AIService";
import NeuroAnalysisResult from "./NeuroAnalysisResult";
import { DEFAULT_ANALYSIS_MODEL, NEURO_MODEL_OPTIONS, OPENAI_LATEST_MODEL } from "../services/modelCatalog";

const { Title, Paragraph, Text } = Typography;

const createDefaultPerformanceInputs = (analysis) => ({
  hookRatePercent: analysis?.performanceReview?.inputs?.hookRatePercent ?? null,
  holdRatePercent: analysis?.performanceReview?.inputs?.holdRatePercent ?? null,
  clickThroughRatePercent:
    analysis?.performanceReview?.inputs?.clickThroughRatePercent ?? null,
  dropOffRatePercent:
    analysis?.performanceReview?.inputs?.dropOffRatePercent ?? null,
  dropOffTimeSec: analysis?.performanceReview?.inputs?.dropOffTimeSec ?? null,
  notes: analysis?.performanceReview?.inputs?.notes || "",
});

const sanitizePerformanceInputs = (inputs = {}) => {
  const toNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };
  return {
    hookRatePercent: toNumber(inputs.hookRatePercent),
    holdRatePercent: toNumber(inputs.holdRatePercent),
    clickThroughRatePercent: toNumber(inputs.clickThroughRatePercent),
    dropOffRatePercent: toNumber(inputs.dropOffRatePercent),
    dropOffTimeSec: toNumber(inputs.dropOffTimeSec),
    notes: String(inputs.notes || "").trim(),
  };
};

function AnalysisPage({
  analysis,
  history,
  onSelectAnalysis,
  tags = [],
  onTagsChange,
  onAnalysisUpdate,
  onAdCopyKnowledgeChange,
  apiKeys,
  aiProfile,
  analysisKnowledge,
  adCopyKnowledge,
  analysisModel,
  generationAspectRatio,
  workspaceId,
  showCard = true,
  showHeader = showCard,
}) {
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [suggestionError, setSuggestionError] = useState("");
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(0);
  const [reviewingPerformance, setReviewingPerformance] = useState(false);
  const [performanceError, setPerformanceError] = useState("");
  const [performanceInputs, setPerformanceInputs] = useState(
    createDefaultPerformanceInputs(analysis),
  );
  const [neuroRunning, setNeuroRunning] = useState(false);
  const [neuroError, setNeuroError] = useState("");
  const [neuroResult, setNeuroResult] = useState(null);
  const [neuroModel, setNeuroModel] = useState(OPENAI_LATEST_MODEL);
  const [neuroStep, setNeuroStep] = useState("");
  const [neuroStepMessage, setNeuroStepMessage] = useState("");
  const [neuroFullResult, setNeuroFullResult] = useState(null);
  const [optimalImage, setOptimalImage] = useState(null);
  const [optimalImageLoading, setOptimalImageLoading] = useState(false);
  const [optimalImageError, setOptimalImageError] = useState("");
  const generatedPromptRef = React.useRef(null);

  const analysisContext = analysis?.masterContext || analysis?.analysis || analysis;
  const segments = Array.isArray(analysisContext?.segments)
    ? analysisContext.segments
    : [];
  const hasSegments = segments.length > 0;
  const activeSegmentIndex = Math.min(
    selectedSegmentIndex,
    Math.max(0, segments.length - 1),
  );
  const activeSegment = hasSegments ? segments[activeSegmentIndex] : null;
  const tagContext = hasSegments
    ? {
        ...activeSegment,
        actor_profile: analysisContext?.actor_profile || {},
        continuity_locks: analysisContext?.continuity_locks || {},
      }
    : analysisContext;

  React.useEffect(() => {
    setSelectedSegmentIndex(0);
  }, [analysis?.id]);

  React.useEffect(() => {
    setPerformanceInputs(createDefaultPerformanceInputs(analysis));
    setPerformanceError("");
    setNeuroResult(null);
    setNeuroError("");
    setNeuroStep("");
    setNeuroStepMessage("");
    setNeuroFullResult(null);
    setOptimalImage(null);
    setOptimalImageLoading(false);
    setOptimalImageError("");
    generatedPromptRef.current = null;
  }, [analysis?.id]);

  React.useEffect(() => {
    let cancelled = false;
    const fetchSuggestions = async () => {
      setSuggestedTags([]);
      setSuggestionError("");
      if (!tagContext) {
        return;
      }
      setSuggesting(true);
      try {
        const aiService = new AIService({
          ...apiKeys,
          aiProfile,
          analysisKnowledge,
          adCopyKnowledge,
          tagProfile: tags,
          generationAspectRatio,
          workspaceId,
        });
        const suggestions = await aiService.suggestTagsFromContext(tagContext);
        if (!cancelled) {
          const existing = new Set(
            (tags || []).map((item) => item.name.trim().toLowerCase()),
          );
          const seen = new Set();
          const unique = [];
          for (const tag of suggestions) {
            const name = tag.name?.trim();
            if (!name) continue;
            const key = name.toLowerCase();
            if (existing.has(key) || seen.has(key)) continue;
            seen.add(key);
            unique.push(tag);
          }
          setSuggestedTags(unique);
        }
      } catch (error) {
        if (!cancelled) {
          setSuggestionError("Could not load tag suggestions.");
        }
      } finally {
        if (!cancelled) {
          setSuggesting(false);
        }
      }
    };

    fetchSuggestions();
    return () => {
      cancelled = true;
    };
  }, [analysis?.id, activeSegmentIndex, tagContext, workspaceId]);

  const handleGenerateOptimalImage = async (promptText) => {
    const prompt = promptText || neuroFullResult?.optimalPrompt?.prompt;
    if (!prompt) return;
    setOptimalImageLoading(true);
    setOptimalImageError("");
    try {
      const aiService = new AIService({
        ...apiKeys,
        aiProfile,
        analysisKnowledge,
        adCopyKnowledge,
        tagProfile: tags,
        generationAspectRatio,
        workspaceId,
      });
      const imageUrl = await aiService.generateImageFromPrompt({
        prompt,
        aspectRatio: generationAspectRatio || "1:1",
      });
      setOptimalImage(imageUrl);
    } catch (error) {
      setOptimalImageError(error?.message || "Image generation failed.");
    } finally {
      setOptimalImageLoading(false);
    }
  };

  // Once the neuro analysis surfaces its "perfect" prompt, generate a GPT image
  // from it so it can be shown above the prompt. Runs once per unique prompt
  // (covers both freshly-run and history-loaded analyses). Must stay above the
  // early return below so hook order is stable across renders.
  React.useEffect(() => {
    const prompt = neuroFullResult?.optimalPrompt?.prompt;
    if (!prompt || generatedPromptRef.current === prompt) return;
    generatedPromptRef.current = prompt;
    handleGenerateOptimalImage(prompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neuroFullResult?.optimalPrompt?.prompt]);

  if (!analysis && (!history || history.length === 0)) {
    return <div style={{ padding: 40 }}>No analysis data selected.</div>;
  }

  const context = analysisContext;
  const baseContext = hasSegments ? activeSegment : context;
  const performanceReview = analysis?.performanceReview || null;
  const isVideo = analysis?.type === "video" || hasSegments;
  const whyItWorks = baseContext
    ? baseContext.why_it_works ||
      baseContext.whyItWorks ||
      baseContext?.script_context?.why_it_works ||
      baseContext?.script_context?.whyItWorks ||
      baseContext?.analysis_context?.why_it_works ||
      baseContext?.analysis_context?.whyItWorks ||
      context?.combined_context?.why_it_works ||
      context?.combined_context?.whyItWorks
    : null;

  const handleAnalyzePerformance = async () => {
    if (!analysis || !context) {
      message.info("Select an analysis first.");
      return;
    }
    const normalizedInputs = sanitizePerformanceInputs(performanceInputs);
    if (normalizedInputs.hookRatePercent == null) {
      message.error(
        isVideo
          ? "Enter a hook rate so the AI can diagnose where attention failed."
          : "Enter a thumb-stop / hook rate so the AI can diagnose first-impression failure.",
      );
      return;
    }
    setReviewingPerformance(true);
    setPerformanceError("");
    try {
      const aiService = new AIService({
        ...apiKeys,
        aiProfile,
        analysisKnowledge,
        adCopyKnowledge,
        tagProfile: tags,
        generationAspectRatio,
        workspaceId,
      });
        const review = await aiService.analyzePoorPerformingCreative(
        context,
        normalizedInputs,
        {
          mediaType: isVideo ? "video" : "image",
          model: analysisModel || DEFAULT_ANALYSIS_MODEL,
        },
      );
      const nextReview = {
        ...review,
        inputs: normalizedInputs,
        analyzedAt: new Date().toISOString(),
        addedToNegativeAt: performanceReview?.addedToNegativeAt || null,
      };
      onAnalysisUpdate?.(analysis.id, (prev) => ({
        ...prev,
        performanceReview: nextReview,
      }));
      message.success("Poor-performance analysis ready.");
    } catch (error) {
      setPerformanceError(error?.message || "Failed to analyze poor performance.");
    } finally {
      setReviewingPerformance(false);
    }
  };

  const handleAddToNegativeKnowledge = () => {
    if (!analysis || !performanceReview) {
      message.info("Run a poor-performance analysis first.");
      return;
    }
    const sourceLabel = `[Poor performer: ${analysis.fileName || "Creative"}]`;
    const currentAvoid = String(adCopyKnowledge?.avoid || "").trim();
    if (currentAvoid.includes(sourceLabel)) {
      message.info("This analysis is already in the negative knowledge base.");
      return;
    }
    const avoidLines = Array.isArray(performanceReview?.avoidRules)
      ? performanceReview.avoidRules
      : [];
    const summaryLine = String(
      performanceReview?.negativeKnowledgeSummary || performanceReview?.summary || "",
    ).trim();
    const block = [
      sourceLabel,
      summaryLine ? `Why to avoid: ${summaryLine}` : "",
      ...avoidLines.map((line) => `- ${line}`),
    ]
      .filter(Boolean)
      .join("\n");
    if (!block.trim()) {
      message.info("No avoid guidance was returned for this creative.");
      return;
    }
    const nextAvoid = [currentAvoid, block].filter(Boolean).join("\n\n");
    onAdCopyKnowledgeChange?.({
      ...(adCopyKnowledge || {}),
      avoid: nextAvoid,
    });
    onAnalysisUpdate?.(analysis.id, (prev) => ({
      ...prev,
      performanceReview: {
        ...(prev?.performanceReview || performanceReview),
        addedToNegativeAt: new Date().toISOString(),
      },
    }));
    message.success("Added to the negative knowledge base.");
  };

  const handleAddSuggestedTag = (tag) => {
    const name = tag.name?.trim();
    if (!name) return;
    const existing = new Set(
      (tags || []).map((item) => item.name.trim().toLowerCase()),
    );
    if (existing.has(name.toLowerCase())) {
      message.info("Tag already exists in your library.");
      return;
    }
    const description =
      tag.description ||
      `A flexible ${name.toLowerCase()} direction that matches the creative vibe.`;
    const next = [
      {
        id: `tag-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name,
        description,
      },
      ...(tags || []),
    ];
    onTagsChange?.(next);
    setSuggestedTags((prev) =>
      prev.filter((item) => item.name?.toLowerCase() !== name.toLowerCase()),
    );
    message.success(`Added "${name}" to your tag library.`);
  };

  const handleRunNeuroAnalysis = async () => {
    if (!analysis || !context) {
      message.info("Select an analysis first.");
      return;
    }
    setNeuroRunning(true);
    setNeuroError("");
    setNeuroStep("");
    setNeuroStepMessage("");
    setNeuroFullResult(null);
    setNeuroResult(null);
    try {
      const aiService = new AIService({
        ...apiKeys,
        aiProfile,
        analysisKnowledge,
        adCopyKnowledge,
        tagProfile: tags,
        generationAspectRatio,
        workspaceId,
      });
      const fullResult = await aiService.runFullNeuroAnalysis(context, {
        mediaType: isVideo ? "video" : "image",
        model: neuroModel,
        onProgress: ({ step, message: msg, tribeResult }) => {
          setNeuroStep(step);
          setNeuroStepMessage(msg || "");
          if (tribeResult) {
            setNeuroFullResult((prev) => ({ ...prev, tribeResult }));
          }
        },
      });
      setNeuroFullResult(fullResult);
      setNeuroResult(fullResult.synthesized);
      message.success("Full neuro analysis complete.");
    } catch (error) {
      setNeuroError(error?.message || "Neuro analysis failed.");
    } finally {
      setNeuroRunning(false);
      setNeuroStep("complete");
    }
  };

  const summaryTab = analysis ? (
    <Row gutter={12}>
      <Col span={24}>
        {analysis?.previewUrl ? (
          <div
            style={{
              width: "100%",
              height: 180,
              background: "rgba(148, 163, 184, 0.12)",
              borderRadius: 8,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {analysis?.type === "image" ? (
              <Image
                src={analysis?.previewUrl}
                preview={{ src: analysis?.previewUrl }}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <video
                src={analysis?.previewUrl}
                controls
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            )}
          </div>
        ) : (
          <Empty description="Preview unavailable" />
        )}
      </Col>
      {hasSegments && activeSegment && (
        <Col span={24}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Text type="secondary">Segment</Text>
            <Select
              size="small"
              value={activeSegmentIndex}
              onChange={(value) => setSelectedSegmentIndex(value)}
              options={segments.map((segment, index) => ({
                value: index,
                label: `Batch ${segment.batchIndex} (${segment.startSec}s-${segment.endSec}s)`,
              }))}
            />
            <Tabs
              size="small"
              items={[
                {
                  key: "environment",
                  label: "Environment",
                  children: (
                    <pre
                      style={{
                        background: "rgba(148, 163, 184, 0.08)",
                        padding: 12,
                        borderRadius: 6,
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        maxHeight: 220,
                        overflow: "auto",
                      }}
                    >
                      {JSON.stringify(activeSegment.environment_context || {}, null, 2)}
                    </pre>
                  ),
                },
                {
                  key: "action",
                  label: "Action",
                  children: (
                    <pre
                      style={{
                        background: "rgba(148, 163, 184, 0.08)",
                        padding: 12,
                        borderRadius: 6,
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        maxHeight: 220,
                        overflow: "auto",
                      }}
                    >
                      {JSON.stringify(activeSegment.action_context || {}, null, 2)}
                    </pre>
                  ),
                },
                {
                  key: "script",
                  label: "Script",
                  children: (
                    <pre
                      style={{
                        background: "rgba(148, 163, 184, 0.08)",
                        padding: 12,
                        borderRadius: 6,
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        maxHeight: 220,
                        overflow: "auto",
                      }}
                    >
                      {JSON.stringify(activeSegment.script_context || {}, null, 2)}
                    </pre>
                  ),
                },
                {
                  key: "continuity",
                  label: "Continuity",
                  children: (
                    <pre
                      style={{
                        background: "rgba(148, 163, 184, 0.08)",
                        padding: 12,
                        borderRadius: 6,
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        maxHeight: 220,
                        overflow: "auto",
                      }}
                    >
                      {JSON.stringify(
                        {
                          actor_profile: context?.actor_profile || {},
                          continuity_locks: context?.continuity_locks || {},
                        },
                        null,
                        2,
                      )}
                    </pre>
                  ),
                },
              ]}
            />
          </Space>
        </Col>
      )}
      <Col span={24}>
        <Divider orientation="left" style={{ margin: "8px 0" }}>
          <BulbOutlined /> Why It Works
        </Divider>
        <Paragraph
          style={{ fontSize: 12, lineHeight: "1.5", marginBottom: 0 }}
          ellipsis={{ rows: 3, expandable: true, symbol: "More" }}
        >
          {whyItWorks || "Detailed psychological analysis loading..."}
        </Paragraph>
      </Col>
      <Col span={24}>
        <Divider orientation="left" style={{ margin: "8px 0" }}>
          Suggested Tags
        </Divider>
        {suggesting ? (
          <Text type="secondary">Generating tag ideas...</Text>
        ) : suggestionError ? (
          <Text type="secondary">{suggestionError}</Text>
        ) : suggestedTags.length > 0 ? (
          <Space size={[6, 6]} wrap>
            {suggestedTags.map((tag) => (
              <Tooltip
                key={tag.name}
                title={tag.description || "Click to add this tag"}
              >
                <Tag
                  color="blue"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleAddSuggestedTag(tag)}
                >
                  {tag.name}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        ) : (
          <Text type="secondary">
            No suggestions yet. Analyze a creative to generate tag ideas.
          </Text>
        )}
      </Col>
    </Row>
  ) : (
    <Alert
      type="info"
      showIcon
      message="Select an analysis"
      description="Use History to choose a past analysis."
    />
  );

  const performanceTab = analysis ? (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <Text type="secondary">
        Diagnose why this creative underperformed. Feed the AI real metrics so it
        can reason about psychology, structure, and what to avoid next time.
      </Text>
      <Row gutter={[8, 8]}>
        <Col span={12}>
          <Text type="secondary">
            {isVideo ? "Hook Rate (%)" : "Thumb-Stop / Hook Rate (%)"}
          </Text>
          <InputNumber
            min={0}
            max={100}
            style={{ width: "100%" }}
            value={performanceInputs.hookRatePercent}
            onChange={(value) =>
              setPerformanceInputs((prev) => ({ ...prev, hookRatePercent: value }))
            }
          />
        </Col>
        <Col span={12}>
          <Text type="secondary">
            {isVideo ? "Hold Rate (%)" : "CTR / Hold Rate (%)"}
          </Text>
          <InputNumber
            min={0}
            max={100}
            style={{ width: "100%" }}
            value={performanceInputs.holdRatePercent}
            onChange={(value) =>
              setPerformanceInputs((prev) => ({ ...prev, holdRatePercent: value }))
            }
          />
        </Col>
        <Col span={12}>
          <Text type="secondary">Click-Through Rate (%)</Text>
          <InputNumber
            min={0}
            max={100}
            style={{ width: "100%" }}
            value={performanceInputs.clickThroughRatePercent}
            onChange={(value) =>
              setPerformanceInputs((prev) => ({
                ...prev,
                clickThroughRatePercent: value,
              }))
            }
          />
        </Col>
        {isVideo ? (
          <>
            <Col span={12}>
              <Text type="secondary">Drop-Off Rate (%)</Text>
              <InputNumber
                min={0}
                max={100}
                style={{ width: "100%" }}
                value={performanceInputs.dropOffRatePercent}
                onChange={(value) =>
                  setPerformanceInputs((prev) => ({
                    ...prev,
                    dropOffRatePercent: value,
                  }))
                }
              />
            </Col>
            <Col span={12}>
              <Text type="secondary">Drop-Off Time (sec)</Text>
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                value={performanceInputs.dropOffTimeSec}
                onChange={(value) =>
                  setPerformanceInputs((prev) => ({
                    ...prev,
                    dropOffTimeSec: value,
                  }))
                }
              />
            </Col>
          </>
        ) : null}
        <Col span={24}>
          <Text type="secondary">Performance Notes</Text>
          <Input.TextArea
            rows={3}
            placeholder="Optional context: weak comments, low saves, bad CTR, wrong audience, offer confusion, etc."
            value={performanceInputs.notes}
            onChange={(event) =>
              setPerformanceInputs((prev) => ({
                ...prev,
                notes: event.target.value,
              }))
            }
          />
        </Col>
      </Row>
      <Space size="small">
        <Button
          type="primary"
          loading={reviewingPerformance}
          onClick={handleAnalyzePerformance}
        >
          Analyze Poor Performance
        </Button>
        <Button
          disabled={!performanceReview}
          onClick={handleAddToNegativeKnowledge}
        >
          Add To Negative Knowledge Base
        </Button>
      </Space>
      {performanceReview?.addedToNegativeAt ? (
        <Text type="secondary">
          Added to negative knowledge base{" "}
          {new Date(performanceReview.addedToNegativeAt).toLocaleString()}.
        </Text>
      ) : null}
      {performanceError ? (
        <Alert type="error" showIcon message={performanceError} />
      ) : null}
      {reviewingPerformance ? (
        <Space size="small">
          <Spin size="small" />
          <Text type="secondary">Diagnosing weak psychology and structure...</Text>
        </Space>
      ) : null}
      {performanceReview ? (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Divider orientation="left" style={{ margin: "8px 0" }}>
            Summary
          </Divider>
          <Paragraph style={{ marginBottom: 0 }}>
            {performanceReview.summary || "No summary returned."}
          </Paragraph>

          <Divider orientation="left" style={{ margin: "8px 0" }}>
            Psychology
          </Divider>
          <List
            size="small"
            bordered
            dataSource={Object.entries(performanceReview.psychology || {}).filter(
              ([, value]) => String(value || "").trim(),
            )}
            renderItem={([key, value]) => (
              <List.Item>
                <Text strong style={{ textTransform: "capitalize" }}>
                  {key.replace(/_/g, " ")}:
                </Text>
                <Text style={{ marginLeft: 8 }}>{String(value)}</Text>
              </List.Item>
            )}
          />

          <Divider orientation="left" style={{ margin: "8px 0" }}>
            Structure
          </Divider>
          <List
            size="small"
            bordered
            dataSource={Object.entries(performanceReview.structure || {}).filter(
              ([, value]) => String(value || "").trim(),
            )}
            renderItem={([key, value]) => (
              <List.Item>
                <Text strong style={{ textTransform: "capitalize" }}>
                  {key.replace(/_/g, " ")}:
                </Text>
                <Text style={{ marginLeft: 8 }}>{String(value)}</Text>
              </List.Item>
            )}
          />

          <Divider orientation="left" style={{ margin: "8px 0" }}>
            Failure Points
          </Divider>
          {Array.isArray(performanceReview.failurePoints) &&
          performanceReview.failurePoints.length > 0 ? (
            <List
              size="small"
              bordered
              dataSource={performanceReview.failurePoints}
              renderItem={(item, index) => (
                <List.Item>
                  <Space direction="vertical" size={2} style={{ width: "100%" }}>
                    <Text strong>
                      {item?.stage || `Failure Point ${index + 1}`}
                    </Text>
                    <Text>{item?.reason || ""}</Text>
                    {item?.evidence ? (
                      <Text type="secondary">{item.evidence}</Text>
                    ) : null}
                  </Space>
                </List.Item>
              )}
            />
          ) : (
            <Text type="secondary">No failure points returned.</Text>
          )}

          <Divider orientation="left" style={{ margin: "8px 0" }}>
            Avoid Next Time
          </Divider>
          {Array.isArray(performanceReview.avoidRules) &&
          performanceReview.avoidRules.length > 0 ? (
            <List
              size="small"
              bordered
              dataSource={performanceReview.avoidRules}
              renderItem={(rule) => <List.Item>{rule}</List.Item>}
            />
          ) : (
            <Text type="secondary">No negative guidance returned.</Text>
          )}
        </Space>
      ) : (
        <Alert
          type="info"
          showIcon
          message="No poor-performance review yet"
          description="Enter real performance metrics and run the analysis to diagnose why this creative fell short."
        />
      )}
    </Space>
  ) : (
    <Alert
      type="info"
      showIcon
      message="Select an analysis"
      description="Use History to choose a past analysis."
    />
  );

  const neuroTab = analysis ? (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <Text type="secondary">
        Neuroscience-powered analysis using Meta Tribe v2 methodology. Cross-references
        your creative with brain activation patterns to explain why certain elements
        trigger specific neural responses, and suggests optimizations.
      </Text>
      <Row gutter={[8, 8]} align="middle">
        <Col>
          <Text type="secondary">Model:</Text>
        </Col>
        <Col flex="auto">
          <Select
            size="small"
            style={{ width: "100%" }}
            value={neuroModel}
            onChange={setNeuroModel}
            options={NEURO_MODEL_OPTIONS}
          />
        </Col>
        <Col>
          <Button
            type="primary"
            loading={neuroRunning}
            icon={<ExperimentOutlined />}
            onClick={handleRunNeuroAnalysis}
          >
            Run Neuro Analysis
          </Button>
        </Col>
      </Row>
      {neuroError ? (
        <Alert type="error" showIcon message={neuroError} />
      ) : null}
      {neuroRunning ? (
        <div style={{ background: "rgba(148, 163, 184, 0.06)", borderRadius: 8, padding: "10px 14px" }}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Space size="small">
              <Spin size="small" />
              <Text strong style={{ fontSize: 13 }}>{neuroStepMessage || "Initializing..."}</Text>
            </Space>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                { key: "tribe", label: "Brain Scan" },
                { key: "delegation", label: "GPT + Gemini" },
                { key: "synthesis", label: "Synthesis" },
                { key: "optimal_prompt", label: "10/10 Prompt" },
              ].map((s) => {
                const done = ["tribe_done", "delegation", "delegation_done", "synthesis", "synthesis_done", "optimal_prompt", "complete"].includes(neuroStep) && (() => {
                  const order = ["tribe", "delegation", "synthesis", "optimal_prompt"];
                  const stepBase = neuroStep.replace("_done", "");
                  return order.indexOf(s.key) < order.indexOf(stepBase) || neuroStep === "complete" || neuroStep === `${s.key}_done`;
                })();
                const active = neuroStep === s.key || neuroStep === `${s.key}_done`;
                return (
                  <Tag
                    key={s.key}
                    color={done ? "green" : active ? "processing" : "default"}
                    style={{ fontSize: 11 }}
                  >
                    {done ? "\u2713" : active ? "\u25CF" : "\u25CB"} {s.label}
                  </Tag>
                );
              })}
            </div>
          </Space>
        </div>
      ) : null}
      {neuroResult ? (
        <NeuroAnalysisResult
          result={neuroResult}
          fullResult={neuroFullResult}
          optimalImage={optimalImage}
          optimalImageLoading={optimalImageLoading}
          optimalImageError={optimalImageError}
          onGenerateOptimalImage={() => handleGenerateOptimalImage()}
        />
      ) : (
        <Alert
          type="info"
          showIcon
          message="No neuro analysis yet"
          description="Select a model and run the analysis to map how this creative activates the brain and get neuroscience-backed improvement suggestions."
        />
      )}
    </Space>
  ) : (
    <Alert
      type="info"
      showIcon
      message="Select an analysis"
      description="Use History to choose a past analysis."
    />
  );

  const content = (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <Space style={{ justifyContent: "space-between", width: "100%" }}>
        {showHeader ? (
          <Title level={5} style={{ margin: 0 }}>
            Summary
          </Title>
        ) : (
          <span />
        )}
        <Space size="small">
          <Button size="small" onClick={() => setHistoryOpen(true)}>
            History
          </Button>
        </Space>
      </Space>
      <Tabs
        size="small"
        items={[
          { key: "summary", label: "Summary", children: summaryTab },
          {
            key: "performance",
            label: "Performance Analysis",
            children: performanceTab,
          },
          {
            key: "neuro",
            label: "Neuro Analysis",
            children: neuroTab,
          },
        ]}
      />

      <Modal
        title="Past Analyses"
        open={historyOpen}
        onCancel={() => setHistoryOpen(false)}
        footer={null}
      >
        {!history || history.length === 0 ? (
          <Empty description="No past analyses yet." />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={history}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button
                    key="view"
                    type={item.id === analysis?.id ? "primary" : "default"}
                    onClick={() => {
                      onSelectAnalysis?.(item);
                      setHistoryOpen(false);
                    }}
                  >
                    View
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={item.fileName || "Creative"}
                  description={
                    item.createdAt
                      ? new Date(item.createdAt).toLocaleString()
                      : "Unknown time"
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Modal>
    </Space>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card size="small" title="Analysis">
      {content}
    </Card>
  );
}

export default AnalysisPage;
